import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { toast } from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import SupportHoursTimeline from './SupportHoursTimeline';
import AdminPeppolInvoices from './AdminPeppolInvoices';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { CAP, roleBadgeClass, roleLabel } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice, inertClasses } from '../utils/PermissionContext';
import { errorText } from '../utils/apiErrors';

// Helper function to safely render Firestore values that might be raw objects containing {stringValue: ...}
const renderValue = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
        if ('stringValue' in val) return val.stringValue;
        if ('integerValue' in val) return val.integerValue;
        if ('doubleValue' in val) return val.doubleValue;
        if ('booleanValue' in val) return String(val.booleanValue);
        if ('nullValue' in val) return '';
        return JSON.stringify(val);
    }
    return String(val);
};

const getFlagEmoji = (countryCode) => {
    if (!countryCode || typeof countryCode !== 'string' || countryCode.length !== 2) return null;
    const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-3 rounded-lg shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)] border border-gray-100">
                {label && <p className="font-semibold text-gray-700 mb-2">{label}</p>}
                <div className="space-y-2">
                    {payload.map((entry, index) => {
                        const color = entry.color || (entry.payload && entry.payload.fill) || '#8884d8';
                        return (
                            <div key={`item-${index}`} className="flex items-center text-sm gap-2">
                                <span className="px-2 py-0.5 rounded text-white text-xs font-medium" style={{ backgroundColor: color }}>
                                    {entry.name}
                                </span>
                                <span style={{ color: color }} className="font-medium">
                                    €{entry.value}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
    return null;
};

const AdminCustomerDetails = ({ googleIdToken, t, language }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [details, setDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('billing'); // billing, logins, orders, transactions, peppol

    const { apiRequest } = useApi(googleIdToken, () => window.location.reload());
    // The AFAS sync is an admin action; the account-settings pills edit the
    // customer record itself. Different capabilities, so they are asked for
    // separately rather than behind one "is admin" flag.
    const { can } = usePermissions();
    const canAdminWrite = can(CAP.ADMIN_WRITE);
    // hasAccess / isPostpaid / isActive are operator flags, not profile fields:
    // they are exactly what permissions.SELF_EDITABLE_CUSTOMER_FIELDS excludes,
    // so the server refuses them from anyone without SELECT_ANY_CUSTOMER.
    // Gated on adminWrite, matching the same pills on the dashboard and the
    // customer list.
    const canEditCustomer = canAdminWrite;
    const [updatingField, setUpdatingField] = useState(null); // 'hasAccess' | 'isPostpaid' | 'isActive'
    const [isSyncingCustomer, setIsSyncingCustomer] = useState(false);
    const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [isLoadingTickets, setIsLoadingTickets] = useState(false);
    const [ticketsFetched, setTicketsFetched] = useState(false);

    const [jobLogs, setJobLogs] = useState([]);
    const [isLoadingJobLogs, setIsLoadingJobLogs] = useState(false);
    const [jobLogsFetched, setJobLogsFetched] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [selectedData, setSelectedData] = useState(null);

    const handleChartClick = (data, titlePrefix) => {
        if (data && data.orders) {
            setSelectedData(data.orders);
            setModalTitle(`${titlePrefix}: ${data.name}`);
            setIsModalOpen(true);
        }
    };

    const handleSyncCustomer = async () => {
        if (isSyncingCustomer) return;
        const custId = renderValue(details?.customer?.customerId) || id;
        if (!custId) return;
        setIsSyncingCustomer(true);
        const loadingToast = toast.loading(t?.starting_customer_sync || 'Syncing customer from AFAS...');
        try {
            const data = await apiRequest(`/afas/customers/${custId}/sync`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            if (data && data.success) {
                if (data.customer) {
                    setDetails(prev => ({ ...prev, customer: { ...prev.customer, ...data.customer } }));
                }
                // Clear caches so the dashboard + main list reflect the change.
                try {
                    sessionStorage.removeItem('admin_customers_cache');
                    Object.keys(sessionStorage).forEach(key => {
                        if (key.startsWith('dashboard_initial_data_')) sessionStorage.removeItem(key);
                    });
                } catch (e) {
                    console.error('Failed to clear sessionStorage after customer sync:', e);
                }
                toast.success(data.message || (t?.customer_synced || 'Customer synced from AFAS.'), { id: loadingToast });
                setSyncConfirmOpen(false);
            } else {
                toast.error(errorText(data, t, t?.customer_sync_failed || 'Failed to sync customer.'), { id: loadingToast });
            }
        } catch (error) {
            console.error('Error syncing customer:', error);
            toast.error(errorText(error, t, t?.customer_sync_failed || 'Failed to sync customer.'), { id: loadingToast });
        } finally {
            setIsSyncingCustomer(false);
        }
    };

    const handleAccountSettingToggle = async (field, currentVal) => {
        if (!details || !details.customer || updatingField) return;

        const newVal = !currentVal;
        const custId = renderValue(details.customer.customerId) || renderValue(details.customer.id) || id;
        if (!custId) return;

        setUpdatingField(field);
        try {
            // Minimal payload: partial update avoids persisting computed fields.
            const data = await apiRequest(`/api/customers/${custId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ [field]: newVal })
            });

            if (data && !data.error) {
                setDetails(prev => ({
                    ...prev,
                    customer: {
                        ...prev.customer,
                        [field]: newVal
                    }
                }));
                // Clear caches so the dashboard + main page reflect the change.
                try {
                    sessionStorage.removeItem('admin_customers_cache');
                    Object.keys(sessionStorage).forEach(key => {
                        if (key.startsWith('dashboard_initial_data_')) {
                            sessionStorage.removeItem(key);
                        }
                    });
                } catch (e) {
                    console.error("Failed to clear sessionStorage after account setting toggle:", e);
                }
                toast.success("Account settings updated.");
            } else {
                toast.error(errorText(data, t, t?.failed_update_account_settings || "Failed to update account settings."));
            }
        } catch (error) {
            console.error("Error updating account settings:", error);
            toast.error("Failed to update account settings.");
        } finally {
            setUpdatingField(null);
        }
    };

    useEffect(() => {
        const fetchDetails = async () => {
            setIsLoading(true);
            try {
                const data = await apiRequest(`/api/admin/customers/${id}/details`);
                if (data && !data.error) {
                    setDetails(data);
                } else if (data && data.error) {
                    toast.error(errorText(data, t));
                }
            } catch (error) {
                console.error("Error fetching customer details:", error);
                toast.error("Failed to load customer details.");
            } finally {
                setIsLoading(false);
            }
        };

        if (googleIdToken && id) {
            fetchDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleIdToken, id]);

    useEffect(() => {
        if (activeTab === 'support_hours' && !ticketsFetched && details?.customer?.customerId) {
            const fetchTickets = async () => {
                setIsLoadingTickets(true);
                try {
                    const data = await apiRequest(`/api/customers/${details.customer.customerId}/tickets`);
                    if (data && !data.error) {
                        setTickets(data);
                    }
                } catch (error) {
                    console.error("Error fetching tickets:", error);
                } finally {
                    setIsLoadingTickets(false);
                    setTicketsFetched(true);
                }
            };
            fetchTickets();
        }
    }, [activeTab, ticketsFetched, details, apiRequest]);

    // Not gated on activeTab: the Logs badge counts these, so it would read 0
    // until the tab is opened.
    useEffect(() => {
        if (!jobLogsFetched && id) {
            const fetchJobLogs = async () => {
                setIsLoadingJobLogs(true);
                try {
                    const data = await apiRequest(`/api/customers/${id}/job_logs`);
                    if (data && !data.error) {
                        setJobLogs(data);
                    }
                } catch (error) {
                    console.error("Error fetching job logs:", error);
                } finally {
                    setIsLoadingJobLogs(false);
                    setJobLogsFetched(true);
                }
            };
            fetchJobLogs();
        }
    }, [jobLogsFetched, id, apiRequest]);

    const filteredJobLogs = useMemo(() => {
        return jobLogs.filter(log => {
            if (log.type === 'ERROR') return true;
            const msg = log.message || '';
            // Filter out specific info messages that are too noisy
            if (msg.startsWith('INFO:')) return false;
            if (msg.match(/has \d+ subscriptions? renewing in/i)) return false;
            return true;
        });
    }, [jobLogs]);

    const cartItemStats = useMemo(() => {
        if (!details || !details.orders || !Array.isArray(details.orders)) return [];
        const cartItemMap = new Map();
        details.orders.forEach(order => {
            (order.cartItems || []).forEach(item => {
                const name = item.name || item.description || 'Unknown Item';
                if (!cartItemMap.has(name)) {
                    cartItemMap.set(name, { count: 0, revenue: 0 });
                }
                const itemData = cartItemMap.get(name);
                
                let linePrice = 0;
                const isFlex = item.isFlexiblePlan === true || item.isFlexiblePlan === 'true' || order.isFlexiblePlanInvoice === true;
                if (isFlex && item.totalPrice !== undefined) {
                    linePrice = parseFloat(item.totalPrice) || 0;
                } else {
                    linePrice = (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 1);
                }
                
                itemData.count += (parseFloat(item.quantity) || 1);
                itemData.revenue += linePrice;
            });
        });

        return Array.from(cartItemMap.entries())
            .map(([name, data]) => ({ name, revenue: parseFloat(data.revenue.toFixed(2)) }))
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 8); // Top 8 items
    }, [details]);

    const monthlyRevenueStats = useMemo(() => {
        if (!details || !details.orders || !Array.isArray(details.orders)) return [];
        const monthlyRevMap = new Map();

        details.orders.forEach(order => {
            if (order.createdAt) {
                const date = new Date(order.createdAt);
                const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                if (!monthlyRevMap.has(monthYear)) {
                    monthlyRevMap.set(monthYear, { renewalsRevenue: 0, extraRevenue: 0, flexRevenue: 0, gcpRevenue: 0, orders: [] });
                }
                const monthData = monthlyRevMap.get(monthYear);

                let renewalSubtotal = 0;
                let extraSubtotal = 0;
                let flexSubtotal = 0;
                let gcpSubtotal = 0;

                (order.cartItems || []).forEach(item => {
                    const isRenewal = item.isRenewal === true || item.isRenewal === 'true';
                    const isFlex = item.isFlexiblePlan === true || item.isFlexiblePlan === 'true' || order.isFlexiblePlanInvoice === true;
                    const isGcp = item.isGcpPlan === true || item.isGcpPlan === 'true' || item.isGcp === true || order.isGcpInvoice === true || order.orderType === 'GCP' || (item.name && item.name.toLowerCase().includes('gcp')) || item.afas_artikelcode === 'GCPCOST';

                    let linePrice = 0;
                    if ((isFlex || isGcp) && item.totalPrice !== undefined) {
                        linePrice = parseFloat(item.totalPrice) || 0;
                    } else {
                        linePrice = (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 1);
                    }

                    if (isGcp) {
                        gcpSubtotal += linePrice;
                    } else if (isFlex) {
                        flexSubtotal += linePrice;
                    } else if (isRenewal) {
                        renewalSubtotal += linePrice;
                    } else {
                        extraSubtotal += linePrice;
                    }
                });

                const totalSubtotal = renewalSubtotal + extraSubtotal + flexSubtotal + gcpSubtotal;
                let orderRenewalRev = 0;
                let orderExtraRev = 0;
                let orderFlexRev = 0;
                let orderGcpRev = 0;

                if (totalSubtotal !== 0) {
                    const renewalRatio = renewalSubtotal / totalSubtotal;
                    const flexRatio = flexSubtotal / totalSubtotal;
                    const gcpRatio = gcpSubtotal / totalSubtotal;
                    orderRenewalRev = (order.totalPrice || 0) * renewalRatio;
                    orderFlexRev = (order.totalPrice || 0) * flexRatio;
                    orderGcpRev = (order.totalPrice || 0) * gcpRatio;
                    orderExtraRev = (order.totalPrice || 0) * (1 - renewalRatio - flexRatio - gcpRatio);
                } else {
                    if (order.isGcpInvoice === true || order.orderType === 'GCP') {
                        orderGcpRev = (order.totalPrice || 0);
                    } else {
                        orderExtraRev = (order.totalPrice || 0);
                    }
                }

                monthData.renewalsRevenue += orderRenewalRev;
                monthData.extraRevenue += orderExtraRev;
                monthData.flexRevenue += orderFlexRev;
                monthData.gcpRevenue += orderGcpRev;
                monthData.orders.push(order);
            }
        });

        const sortedMonths = Array.from(monthlyRevMap.keys()).sort();
        return sortedMonths.map(month => ({
            name: month,
            Renewals: parseFloat(monthlyRevMap.get(month).renewalsRevenue.toFixed(2)),
            'Extra Orders': parseFloat(monthlyRevMap.get(month).extraRevenue.toFixed(2)),
            'Flex Plans': parseFloat(monthlyRevMap.get(month).flexRevenue.toFixed(2)),
            GCP: parseFloat((monthlyRevMap.get(month).gcpRevenue || 0).toFixed(2)),
            orders: monthlyRevMap.get(month).orders,
        }));
    }, [details]);

    if (isLoading) {
        return (
            <div className="p-4 sm:p-8 space-y-8 max-w-7xl mx-auto animate-pulse select-none pointer-events-none">
                {/* Header & Back Action */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <button className="flex items-center text-sm font-bold text-gray-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        {t?.cd_back_to_customers || 'Back to Customers'}
                    </button>
                    <div className="h-6 w-32 bg-primary/10 rounded border border-primary/5"></div>
                </div>

                {/* Unified Customer Profile Card Skeleton */}
                <div className="bg-white rounded-2xl shadow-sm border border-primary/5 overflow-hidden">
                    {/* Header portion */}
                    <div className="p-6 border-b border-primary/5 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-primary/5">
                        <div className="space-y-3 flex-grow w-full md:w-auto">
                            {/* Title line */}
                            <div className="h-8 bg-primary/20 rounded-lg w-2/3 md:w-1/3"></div>
                            {/* Tags row */}
                            <div className="flex gap-3">
                                <div className="h-5 bg-primary/10 rounded-full w-24"></div>
                                <div className="h-5 bg-primary/15 rounded-full w-20"></div>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                            {/* Left Side Info Skeleton */}
                            <div className="lg:col-span-7 bg-white rounded-xl border border-primary/5 p-6 space-y-5">
                                <div className="h-5 bg-primary/15 rounded-md w-1/3 mb-4"></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5"><div className="h-3 bg-primary/10 rounded w-16"></div><div className="h-4 bg-primary/15 rounded w-28"></div></div>
                                    <div className="space-y-1.5"><div className="h-3 bg-primary/10 rounded w-20"></div><div className="h-4 bg-primary/15 rounded w-24"></div></div>
                                    <div className="space-y-1.5"><div className="h-3 bg-primary/10 rounded w-12"></div><div className="h-4 bg-primary/15 rounded w-16"></div></div>
                                    <div className="space-y-1.5"><div className="h-3 bg-primary/10 rounded w-24"></div><div className="h-4 bg-primary/15 rounded w-20"></div></div>
                                </div>
                                <div className="space-y-1.5"><div className="h-3 bg-primary/10 rounded w-20"></div><div className="h-4 bg-primary/15 rounded w-4/5"></div></div>
                            </div>

                            {/* Right Side Prepaid Skeleton */}
                            <div className="lg:col-span-5 bg-white rounded-xl border border-primary/5 p-6 space-y-5">
                                <div className="h-5 bg-primary/15 rounded-md w-1/3 mb-4"></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 text-center space-y-2">
                                        <div className="h-3 bg-primary/10 rounded mx-auto w-20"></div>
                                        <div className="h-7 bg-primary/20 rounded mx-auto w-12"></div>
                                    </div>
                                    <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 text-center space-y-2">
                                        <div className="h-3 bg-primary/10 rounded mx-auto w-20"></div>
                                        <div className="h-7 bg-primary/20 rounded mx-auto w-12"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Subscriptions Table Skeleton */}
                        <div className="space-y-4">
                            <div className="h-5 bg-primary/15 rounded-md w-1/4"></div>
                            <div className="rounded-xl border border-primary/5 overflow-hidden">
                                <div className="h-10 bg-primary/20"></div>
                                <div className="p-4 space-y-3">
                                    <div className="h-4 bg-primary/10 rounded w-full"></div>
                                    <div className="h-4 bg-primary/10 rounded w-5/6"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Debtor comparison block skeleton */}
                <div className="bg-primary/5 border border-primary/10 rounded-2xl p-6 space-y-4">
                    <div className="h-6 bg-primary/15 rounded-md w-1/4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white rounded-xl p-4 border border-primary/5 h-24"></div>
                        <div className="bg-white rounded-xl p-4 border border-primary/5 h-24"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!details || !details.customer) {
        return (
            <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-bold text-gray-800">{t?.cd_customer_not_found || 'Customer Not Found'}</h3>
                <p className="text-gray-500 mt-2">{t?.cd_customer_not_found_desc || 'The selected customer record could not be loaded.'}</p>
                <button
                    onClick={() => navigate('/admin/customers')}
                    className="mt-6 bg-primary hover:bg-primary-hover text-white font-bold py-2 px-6 rounded-lg transition duration-200"
                >
                    {t?.cd_back_to_customers || 'Back to Customers'}
                </button>
            </div>
        );
    }

    const { customer, billingInfo, loginHistory, orders, transactions } = details;
    const billingRecord = billingInfo && billingInfo.length > 0 ? billingInfo[0] : null;

    // Defensively parse values
    const customerNameStr = renderValue(customer.customerName || customer.name);
    const customerDomainStr = renderValue(customer.customerDomain);
    const customerDebtorStr = renderValue(customer.afasDebtorNum).trim();
    const customerIdStr = renderValue(customer.customerId);

    const billingDebtorStr = billingRecord ? renderValue(billingRecord.DebtorNr).trim() : '';

    let comparisonState = 'missing'; // match, mismatch, missing
    if (customerDebtorStr && billingDebtorStr) {
        comparisonState = customerDebtorStr === billingDebtorStr ? 'match' : 'mismatch';
    } else if (customerDebtorStr || billingDebtorStr) {
        comparisonState = 'mismatch';
    }

    return (
        <div className="p-4 sm:p-8 space-y-8 max-w-7xl mx-auto">
            <ConfirmModal
                isOpen={syncConfirmOpen}
                title={t?.sync_customer_title || 'Sync this customer?'}
                message={t?.sync_customer_message || 'Pulls the latest debtor name, address, VAT number and contact e-mails for this customer from AFAS.\n\nExisting contact e-mails are kept; only missing ones are added.'}
                confirmLabel={t?.sync_now || 'Sync now'}
                cancelLabel={t?.cancel || 'Cancel'}
                onConfirm={handleSyncCustomer}
                onCancel={() => { if (!isSyncingCustomer) setSyncConfirmOpen(false); }}
                busy={isSyncingCustomer}
                tone="primary"
            />
            {/* Header & Back Action */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <button
                    onClick={() => navigate('/admin/customers')}
                    className="flex items-center text-sm font-bold text-gray-500 hover:text-primary transition duration-150"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t?.cd_back_to_customers || 'Back to Customers'}
                </button>
            </div>

            <ReadOnlyNotice t={t} />

            {/* Unified Customer Profile Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header portion */}
                <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 bg-gray-50/5">
                    <div>
                        <div className="flex flex-wrap items-center gap-4">
                            <h1 className="text-3xl font-black text-gray-900 tracking-tight">{customerNameStr || (t?.cd_no_name || 'No Name')}</h1>
                            {customerDomainStr && (
                                <span className="text-gray-500 font-medium text-sm flex items-center border border-gray-200 bg-white px-2.5 py-0.5 rounded-md shadow-sm">
                                    {customerDomainStr}
                                </span>
                            )}
                            <div className="text-sm font-mono text-gray-500 bg-gray-100 px-3 py-1 rounded border border-gray-200">
                                {customer.customerId ? `ID: ${renderValue(customer.customerId)}` : `Debtor: ${renderValue(customer.afasDebtorNum || customer.id)}`}
                            </div>
                        </div>
                    </div>
                    {canAdminWrite && <button
                        onClick={() => setSyncConfirmOpen(true)}
                        disabled={isSyncingCustomer}
                        title={t?.sync_customer_tooltip || 'Sync this customer from AFAS now'}
                        className={`inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition-colors shrink-0 self-start md:self-auto ${isSyncingCustomer ? 'bg-primary/60 cursor-not-allowed' : 'bg-primary hover:bg-opacity-90'}`}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isSyncingCustomer ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {isSyncingCustomer ? (t?.syncing || 'Syncing…') : (t?.sync_now || 'Sync now')}
                    </button>}
                </div>

                {/* Account Settings. Each pill is status *and* toggle, so a
                    caller who cannot edit the customer keeps seeing the state
                    as an inert pill — hiding them would hide whether the
                    account has access, is postpaid, or is active. */}
                <div className="px-6 pt-6">
                    <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100 flex items-center justify-between flex-wrap gap-4">
                        <h2 className="text-base font-extrabold text-gray-800">{t?.cd_account_settings || 'Account Settings'}</h2>
                        <div className="flex items-center gap-3 flex-wrap">
                            {[
                                { field: 'hasAccess', onLabel: (t?.cd_has_access || 'Has Access'), offLabel: (t?.cd_no_access || 'No Access'), onClass: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200 hover:border-green-400', offClass: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 hover:border-gray-400', dotOn: 'bg-green-600', dotOff: 'bg-gray-500' },
                                { field: 'isPostpaid', onLabel: (t?.postpaid || 'Postpaid'), offLabel: (t?.prepaid || 'Prepaid'), onClass: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200 hover:border-amber-400', offClass: 'bg-teal-100 text-teal-800 border-teal-300 hover:bg-teal-200 hover:border-teal-400', dotOn: 'bg-amber-600', dotOff: 'bg-teal-600' },
                                { field: 'isActive', onLabel: (t?.active || 'Active'), offLabel: (t?.inactive || 'Inactive'), onClass: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200 hover:border-green-400', offClass: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 hover:border-gray-400', dotOn: 'bg-green-600', dotOff: 'bg-gray-500' },
                                { field: 'isAutoInvoiceEnabled', onLabel: (t?.auto_invoice_on || 'Auto Invoice ON'), offLabel: (t?.auto_invoice_off || 'Auto Invoice OFF'), onClass: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-200 hover:border-green-400', offClass: 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200 hover:border-gray-400', dotOn: 'bg-green-600', dotOff: 'bg-gray-500' },
                            ].map(({ field, onLabel, offLabel, onClass, offClass, dotOn, dotOff }) => {
                                // hasAccess/isPostpaid default false; isActive and isAutoInvoiceEnabled default visible (true) unless explicitly false.
                                const isOn = (field === 'isActive' || field === 'isAutoInvoiceEnabled') ? customer[field] !== false : !!customer[field];
                                return (
                                    <button
                                        key={field}
                                        type="button"
                                        onClick={() => handleAccountSettingToggle(field, isOn)}
                                        disabled={!canEditCustomer || updatingField !== null}
                                        aria-pressed={isOn}
                                        title={canEditCustomer
                                            ? `${t?.cd_toggle || 'Toggle'} (${isOn ? onLabel : offLabel})`
                                            : undefined}
                                        className={`text-xs font-bold uppercase px-3 py-1.5 rounded-full border transition-all duration-200 select-none flex items-center gap-1.5 focus:outline-none ${!canEditCustomer ? 'cursor-default' : updatingField !== null ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'
                                            } ${inertClasses(isOn ? onClass : offClass, !canEditCustomer)}`}
                                    >
                                        <span className={`h-1.5 w-1.5 rounded-full ${isOn ? dotOn : dotOff}`}></span>
                                        {isOn ? onLabel : offLabel}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Details portion */}
                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Address & Contact Information Card */}
                        <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100 space-y-4">
                            <h3 className="text-base font-extrabold text-gray-800 flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                {t?.cd_customer_details || 'Customer Details'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_street_address || 'Street Address'}</span>
                                    <span className="font-semibold text-gray-800">{renderValue(customer.address1) || 'N/A'}</span>
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_city_postal || 'City / Postal Code'}</span>
                                    <span className="font-semibold text-gray-800">{renderValue(customer.address2) || 'N/A'}</span>
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_customer_type || 'Customer Type'}</span>
                                    <span className="font-semibold text-gray-800">{renderValue(customer.customerType) || 'N/A'}</span>
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.vat_number || 'VAT Number'}</span>
                                    <span className="font-semibold text-gray-800">{renderValue(customer.vatNumber) || 'N/A'}</span>
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_country || 'Country'}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-gray-800">{renderValue(customer.country) || 'N/A'}</span>
                                        {customer.country && typeof renderValue(customer.country) === 'string' && renderValue(customer.country).length === 2 && (
                                            <span className="text-lg leading-none" title={renderValue(customer.country)}>
                                                {getFlagEmoji(renderValue(customer.country))}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-0.5">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_administratie || 'Administratie'}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-gray-800">{renderValue(customer.administratie) || 'N/A'}</span>
                                        {customer.unitId != null && (
                                            <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded shadow-sm">{customer.unitId}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-0.5 sm:col-span-2">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_computed_vat_code || 'Computed VAT Code'}</span>
                                    <div className="flex items-center gap-2">
                                        <span className={`font-semibold ${customer.vatCode && customer.vatCode.startsWith('Error') ? 'text-red-500' : 'text-gray-800'}`}>
                                            {renderValue(customer.vatCode) || 'N/A'}
                                        </span>
                                        {(customer.vatCode === '1a' || customer.vatCode === 'V03') && (
                                            <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded shadow-sm">21%</span>
                                        )}
                                        {(customer.vatCode === '3bS' || customer.vatCode === '3aS' || customer.vatCode === 'V07') && (
                                            <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded shadow-sm">0%</span>
                                        )}
                                    </div>
                                </div>
                                <div className="space-y-1 sm:col-span-2">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_contact_email || 'Contact Email'}</span>
                                    <div className="flex flex-wrap gap-1.5 mt-1">
                                        {(() => {
                                            const emailStr = renderValue(customer.contactEmail);
                                            if (!emailStr || emailStr.trim() === 'N/A' || emailStr.trim() === '') {
                                                return <span className="font-semibold text-gray-800">N/A</span>;
                                            }
                                            const emails = emailStr.split(',').map(e => e.trim()).filter(Boolean);
                                            if (emails.length === 0) {
                                                return <span className="font-semibold text-gray-800">N/A</span>;
                                            }
                                            return emails.map((email, idx) => (
                                                <span
                                                    key={idx}
                                                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100 shadow-sm"
                                                >
                                                    {email}
                                                </span>
                                            ));
                                        })()}
                                    </div>
                                </div>
                                <div className="space-y-0.5 sm:col-span-2">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_emergency_email || 'Emergency Email'}</span>
                                    <span className="font-semibold text-gray-800">{renderValue(customer.emergencyEmail) || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {/* Top Products Spent Card */}
                            <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100 flex flex-col h-[360px]">
                                <h3 className="text-base font-extrabold text-gray-800 flex items-center mb-4 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    {t?.cd_top_products_spent || 'Top Products (Revenue)'}
                                </h3>
                                <div className="flex-grow min-h-0 relative -ml-4">
                                    {cartItemStats.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={cartItemStats} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                                <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                                <YAxis dataKey="name" type="category" tick={{ fill: '#374151', fontSize: 11 }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} width={150} tickFormatter={(val) => val && val.length > 25 ? `${val.substring(0, 25)}...` : val} />
                                                <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                                <Bar dataKey="revenue" fill="#E84A5F" radius={[0, 4, 4, 0]} barSize={20} name={t?.revenue || 'Revenue'} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="flex h-full items-center justify-center text-sm text-gray-400 italic text-center px-4">
                                            {t?.cd_no_order_data || 'No order data available for this customer.'}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Monthly Revenue Card */}
                            <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100 flex flex-col h-[360px]">
                                <h3 className="text-base font-extrabold text-gray-800 flex items-center mb-4 shrink-0">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    {t?.monthly_revenue || 'Monthly Revenue (€)'}
                                </h3>
                                <div className="flex-grow min-h-0 relative">
                                    {monthlyRevenueStats.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={monthlyRevenueStats} margin={{ top: 20, right: 10, left: -20, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                                <XAxis dataKey="name" tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                                                <YAxis tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(val) => `€${val}`} />
                                                <RechartsTooltip cursor={{ fill: '#F3F4F6' }} content={<CustomTooltip />} />
                                                <Legend layout="horizontal" verticalAlign="top" align="right" wrapperStyle={{ fontSize: '11px', paddingBottom: '10px' }} />
                                                <Bar dataKey="Renewals" fill="#0F9D58" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                                <Bar dataKey="Extra Orders" fill="#1A73E8" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                                <Bar dataKey="Flex Plans" fill="#FFBB28" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                                <Bar dataKey="GCP" fill="#9333EA" radius={[4, 4, 0, 0]} barSize={20} onClick={(data) => handleChartClick(data, t?.monthly_revenue || 'Monthly Revenue')} className="cursor-pointer hover:opacity-80 transition-opacity" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="flex h-full items-center justify-center text-sm text-gray-400 italic text-center px-4">
                                            {t?.cd_no_order_data || 'No order data available for this customer.'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Subscriptions Table Section */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-bold text-gray-800 flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            {t?.cd_active_subscriptions || 'Active Google Workspace Subscriptions'}
                        </h3>
                        {customer.subscriptions && customer.subscriptions.length > 0 ? (
                            <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-xs">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-primary text-white">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_sub_product || 'Product / SKU Name'}</th>
                                            <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_subscription_id || 'Subscription ID'}</th>
                                            <th className="px-6 py-3 text-xs font-bold text-center uppercase tracking-wider">{t?.cd_purchased || 'Purchased'}</th>
                                            <th className="px-6 py-3 text-xs font-bold text-center uppercase tracking-wider">{t?.cd_assigned || 'Assigned'}</th>
                                            <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_renewal_date || 'Renewal Date'}</th>
                                            <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_commitment_plan || 'Commitment Plan'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {customer.subscriptions.map((sub, idx) => (
                                            <tr key={sub.subscriptionId || idx} className="hover:bg-gray-50/50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                                    {renderValue(sub.skuName) || 'Google Workspace'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-xs text-gray-400">
                                                    {renderValue(sub.subscriptionId) || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-bold text-gray-750">
                                                    {renderValue(sub.purchasedLicenses) || '0'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-center font-bold text-gray-500">
                                                    {renderValue(sub.assignedLicenses) || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {renderValue(sub.renewalDate) ? (
                                                        <div className="flex items-center gap-2">
                                                            <span>{renderValue(sub.renewalDate)}</span>
                                                            {sub.renewalType && (
                                                                <span className={`px-1.5 py-0.5 rounded text-xs font-bold border uppercase whitespace-nowrap ${
                                                                    sub.renewalType === 'AUTO_RENEW' 
                                                                        ? 'text-red-500 border-red-500'
                                                                        : 'text-gray-500 border-gray-400 bg-gray-50'
                                                                }`}>
                                                                    {sub.renewalType.replace(/_/g, ' ')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className="px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-gray-100 border border-gray-200">
                                                        {renderValue(sub.planName) || 'N/A'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="bg-gray-50 rounded-xl p-8 text-center border border-dashed border-gray-200">
                                <p className="text-gray-500 text-sm">{t?.cd_no_subscriptions || "No active subscriptions detected under this customer's profile."}</p>
                            </div>
                        )}
                    </div>
                </div></div>

            {/* Debtor ID Audit Panel */}
            <div className={`rounded-2xl border p-6 shadow-sm overflow-hidden relative ${comparisonState === 'match' ? 'bg-emerald-50/50 border-emerald-200/60' :
                    comparisonState === 'mismatch' ? 'bg-rose-50/50 border-rose-200/60' :
                        'bg-amber-50/50 border-amber-200/60'
                }`}>
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                    <div className="space-y-2 max-w-2xl">
                        <div className="flex items-center gap-3">
                            <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full ${comparisonState === 'match' ? 'bg-emerald-100 text-emerald-800' :
                                    comparisonState === 'mismatch' ? 'bg-rose-100 text-rose-800' :
                                        'bg-amber-100 text-amber-800'
                                }`}>
                                {comparisonState === 'match' ? (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                ) : comparisonState === 'mismatch' ? (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                ) : (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01" />
                                    </svg>
                                )}
                            </span>
                            <h2 className="text-xl font-bold text-gray-800">{t?.debtor_comparison || 'Debtor Number Comparison'}</h2>
                        </div>
                        <p className="text-sm text-gray-600">
                            {comparisonState === 'match' && (t?.cd_debtor_match_desc || "Verification Successful. The debtor number defined in the customer record aligns perfectly with AFAS ERP Billing data.")}
                            {comparisonState === 'mismatch' && (t?.cd_debtor_mismatch_desc || "Verification Warning! A mismatch has been detected between the customer document debiteurnummer and the connected AFAS database. Please review the records below.")}
                            {comparisonState === 'missing' && (t?.cd_debtor_missing_desc || "Partially complete data. Please verify if the billing record has been imported from AFAS or if debtor numbers are configured.")}
                        </p>
                    </div>

                    {/* Status Badge */}
                    <span className={`px-4 py-2 text-sm font-bold rounded-xl border tracking-wide uppercase shadow-sm ${comparisonState === 'match' ? 'bg-emerald-600 text-white border-emerald-600' :
                            comparisonState === 'mismatch' ? 'bg-rose-600 text-white border-rose-600 animate-pulse' :
                                'bg-amber-500 text-white border-amber-500'
                        }`}>
                        {comparisonState === 'match' && (t?.debtor_match || 'Synced Match')}
                        {comparisonState === 'mismatch' && (t?.debtor_mismatch || 'Debtor Mismatch')}
                        {comparisonState === 'missing' && (t?.debtor_missing || 'Missing Data')}
                    </span>
                </div>

                {/* Comparative side-by-side display */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-xs flex justify-between items-center">
                        <div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_customer_debtor_label || 'Customer collection afasDebtorNum'}</span>
                            <span className="text-2xl font-black text-gray-800">{customerDebtorStr || <span className="text-gray-300 italic font-medium">{t?.cd_none || 'None'}</span>}</span>
                        </div>
                        <span className="text-xs font-mono font-bold text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">customers</span>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-xs flex justify-between items-center">
                        <div>
                            <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_billing_debtor_label || 'AFAS billing DebtorNr'}</span>
                            <span className="text-2xl font-black text-gray-800">{billingDebtorStr || <span className="text-gray-300 italic font-medium">{t?.cd_no_record_linked || 'No Record Linked'}</span>}</span>
                        </div>
                        <span className="text-xs font-mono font-bold text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">afas_billing_info</span>
                    </div>
                </div>
            </div>

            {/* Dashboard Tabs navigation */}
            <div className="border-b border-gray-200">
                <nav className="flex flex-wrap gap-2 -mb-px">
                    <button
                        onClick={() => setActiveTab('billing')}
                        className={`py-3 px-5 border-b-2 text-sm font-bold transition-all duration-150 ${activeTab === 'billing'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        {t?.billing_info_tab || 'AFAS Billing Info'}
                        {billingRecord && <span className="ml-2 bg-emerald-100 text-emerald-800 text-[11px] font-bold px-1.5 py-0.5 rounded-full">{t?.cd_linked || 'Linked'}</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('logins')}
                        className={`py-3 px-5 border-b-2 text-sm font-bold transition-all duration-150 ${activeTab === 'logins'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        {t?.login_history_tab || 'Login History'}
                        <span className="ml-2 bg-gray-100 text-gray-600 text-[11px] font-bold px-1.5 py-0.5 rounded-full">{loginHistory?.length || 0}</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('orders')}
                        className={`py-3 px-5 border-b-2 text-sm font-bold transition-all duration-150 ${activeTab === 'orders'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        {t?.orders_tab || 'Orders'}
                        <span className="ml-2 bg-gray-100 text-gray-600 text-[11px] font-bold px-1.5 py-0.5 rounded-full">{orders?.length || 0}</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('transactions')}
                        className={`py-3 px-5 border-b-2 text-sm font-bold transition-all duration-150 ${activeTab === 'transactions'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        {t?.transactions_tab || 'Transactions'}
                        <span className="ml-2 bg-gray-100 text-gray-600 text-[11px] font-bold px-1.5 py-0.5 rounded-full">{transactions?.length || 0}</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('support_hours')}
                        className={`py-3 px-5 border-b-2 text-sm font-bold transition-all duration-150 ${activeTab === 'support_hours'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        {t?.support_hours_tab || 'Supporturen'}
                    </button>
                    <button
                        onClick={() => setActiveTab('peppol')}
                        className={`py-3 px-5 border-b-2 text-sm font-bold transition-all duration-150 ${activeTab === 'peppol'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        {t?.peppol_tab || 'Peppol'}
                        <span className="ml-2 bg-gray-100 text-gray-600 text-[11px] font-bold px-1.5 py-0.5 rounded-full">
                            {details?.customer?.administratie === 'Xebia Belgium B.V.' ? (orders?.length || 0) : (orders?.filter(o => o.peppolInvoice)?.length || 0)}
                        </span>
                    </button>
                    <button
                        onClick={() => setActiveTab('job_logs')}
                        className={`py-3 px-5 border-b-2 text-sm font-bold transition-all duration-150 ${activeTab === 'job_logs'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        {t?.logs_tab || 'Logs'}
                        <span className="ml-2 bg-gray-100 text-gray-600 text-[11px] font-bold px-1.5 py-0.5 rounded-full">{filteredJobLogs?.length || 0}</span>
                    </button>
                </nav>
            </div>

            {/* Tab Contents */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {activeTab === 'support_hours' && (
                    <div className="p-6 h-[800px] flex flex-col">
                        <SupportHoursTimeline
                            tickets={tickets}
                            isLoading={isLoadingTickets}
                            orders={orders}
                            isLoadingOrders={false}
                            t={t}
                            isAdmin={true}
                        />
                    </div>
                )}

                {/* TAB 1: AFAS BILLING INFO */}
                {activeTab === 'billing' && (
                    <div className="p-6">
                        {billingRecord ? (
                            <div className="space-y-6">
                                <div className="border-b border-gray-100 pb-4">
                                    <h3 className="text-lg font-bold text-gray-800">{t?.cd_linked_erp_contract || 'Linked ERP Billing Contract'}</h3>
                                    <p className="text-xs text-gray-500 mt-1">{t?.cd_billing_source_note || 'Sourced from Firestore collection `afas_billing_information` mapping by Cloud_Indentity_Id'}</p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_end_customer_name || 'End Customer Name'}</span>
                                        <span className="text-sm font-bold text-gray-800">{renderValue(billingRecord.EndCustomerName) || 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_end_customer_number || 'End Customer Number'}</span>
                                        <span className="text-sm font-bold text-gray-800">{renderValue(billingRecord.EndCustomerNr) || 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_debtor_number || 'Debtor Number (DebtorNr)'}</span>
                                        <span className="text-sm font-bold text-gray-800">{renderValue(billingRecord.DebtorNr) || 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_cloud_identity_id || 'Cloud Identity ID'}</span>
                                        <span className="text-sm font-mono font-bold text-gray-800">{renderValue(billingRecord.Cloud_Indentity_Id) || 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_domain || 'Domain'}</span>
                                        <span className="text-sm font-bold text-gray-800">{renderValue(billingRecord.Domain) || 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_admin_number_name || 'Admin Number / Name'}</span>
                                        <span className="text-sm font-bold text-gray-800">{billingRecord.AdminNr ? `${renderValue(billingRecord.AdminNr)} - ${renderValue(billingRecord.AdminName) || ''}` : 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_purchase_order || 'Purchase Order (PO_Number)'}</span>
                                        <span className="text-sm font-bold text-gray-800 bg-gray-50 px-2 py-0.5 rounded border border-gray-100 inline-block">{renderValue(billingRecord.PO_Number) || 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_contract_start_date || 'Contract Start Date'}</span>
                                        <span className="text-sm font-bold text-gray-800">{renderValue(billingRecord.Contract_Start_Date) || 'N/A'}</span>
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{t?.cd_billing_profile_id || 'Billing Profile ID'}</span>
                                        <span className="text-sm font-mono font-bold text-gray-500">{renderValue(billingRecord.id) || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-8 text-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p className="text-gray-500 font-medium">{t?.cd_no_billing_record || 'No matching billing record was found under the ID'} `{customerIdStr || renderValue(customer.id)}`.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB 2: LOGIN HISTORY */}
                {activeTab === 'logins' && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-primary text-white">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_timestamp || 'Timestamp'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_name || 'Name'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_email || 'Email'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_role || 'Role'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_details || 'Details'}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {loginHistory && loginHistory.length > 0 ? (
                                    loginHistory.map((login, index) => (
                                        <tr key={login.id || index} className="hover:bg-gray-50/50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                                                {login.timestamp ? new Date(login.timestamp).toLocaleString() : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                {renderValue(login.name) || (t?.cd_unknown || 'Unknown')}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {renderValue(login.email) || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {/* Shared role -> colour map, which also
                                                    covers the superseded vocabulary still
                                                    present on older login_history rows. */}
                                                <span className={`px-2 py-0.5 inline-flex text-xs font-bold leading-5 rounded-full ${roleBadgeClass(renderValue(login.role))}`}>
                                                    {roleLabel(renderValue(login.role)) || (t?.cd_unknown || 'Unknown')}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-400 font-mono">
                                                IP: {renderValue(login.ip) || 'N/A'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500">
                                            {t?.cd_no_logins || 'No logins recorded for domain'} `{customerDomainStr}`.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 3: ORDERS */}
                {activeTab === 'orders' && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-primary text-white">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_order_id || 'Order ID'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_date_created || 'Date Created'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_items_count || 'Items Count'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_total_charge || 'Total Charge'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_payment_method || 'Payment Method'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_sync_state || 'Sync State'}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {orders && orders.length > 0 ? (
                                    orders.map((order, index) => {
                                        const count = order.cartItems ? order.cartItems.length : 0;
                                        return (
                                            <tr key={order.id || index} className="hover:bg-gray-50/50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-bold text-gray-900">
                                                    <Link to={`/admin/afas-transactions/${order.id}`} className="text-primary hover:text-indigo-800 hover:underline transition-colors flex items-center gap-1" title={t?.cd_view_afas_trx || 'View AFAS Transaction Details'}>
                                                        {renderValue(order.id)}
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                        </svg>
                                                    </Link>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-bold">
                                                    {count} {t?.cd_items || 'items'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-black">
                                                    €{Number(order.totalPrice || 0).toFixed(2)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase">
                                                    {renderValue(order.paymentMethod) || 'Stripe'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    {order.afasJournalEntryId ? (
                                                        <span className="px-2 py-0.5 inline-flex text-xs font-bold bg-emerald-100 text-emerald-800 rounded-full border border-emerald-200">
                                                            {t?.cd_synced || 'Synced'} (ID: {renderValue(order.afasJournalEntryId)})
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 inline-flex text-xs font-bold bg-amber-100 text-amber-800 rounded-full border border-amber-200">
                                                            {t?.cd_pending_sync || 'Pending Sync'}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                                            {t?.cd_no_orders || 'No orders placed by customer ID'} `{customerIdStr || renderValue(customer.id)}`.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 4: TRANSACTIONS */}
                {activeTab === 'transactions' && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-primary text-white">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_transaction_id || 'Transaction ID'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_timestamp || 'Timestamp'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_plan_name || 'Plan Name'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_change_details || 'Change Details'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_type || 'Type'}</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">{t?.cd_subscription_id || 'Subscription ID'}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {transactions && transactions.length > 0 ? (
                                    transactions.map((txn, index) => (
                                        <tr key={txn.id || index} className="hover:bg-gray-50/50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-bold text-gray-900">
                                                <Link
                                                    to={`/admin/afas-transactions/${renderValue(txn.id)}`}
                                                    className="text-primary hover:text-indigo-800 hover:underline transition-colors flex items-center gap-1"
                                                    title={t?.cd_view_afas_trx || 'View AFAS Transaction Details'}
                                                >
                                                    {renderValue(txn.id)}
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </Link>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {txn.timestamp ? new Date(txn.timestamp).toLocaleString() : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                {renderValue(txn.planName) || 'Google Workplace Plan'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold">
                                                <span className={txn.changeAmount >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                                                    {txn.changeAmount >= 0 ? `+${renderValue(txn.changeAmount)}` : renderValue(txn.changeAmount)} {t?.cd_licenses || 'Licenses'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 uppercase">
                                                <span className="px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-gray-100 border border-gray-200">
                                                    {renderValue(txn.transactionType) || 'LICENSE_CHANGE'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 font-mono text-xs">
                                                {renderValue(txn.subscriptionId) || 'N/A'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-8 text-center text-sm text-gray-500">
                                            {t?.cd_no_transactions || 'No seat licensing transactions recorded for customer ID'} `{customerIdStr || renderValue(customer.id)}`.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* TAB 5: PEPPOL */}
                {activeTab === 'peppol' && (
                    <div className="overflow-x-auto bg-gray-50 min-h-[500px]">
                        <AdminPeppolInvoices t={t} language={language} customerId={renderValue(details?.customer?.customerId) || id} />
                    </div>
                )}

                {/* TAB 6: JOB LOGS */}
                {activeTab === 'job_logs' && (
                    <div className="overflow-x-auto bg-gray-50 min-h-[500px] p-6">
                        {isLoadingJobLogs ? (
                            <div className="flex justify-center items-center h-48">
                                <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        ) : filteredJobLogs.length > 0 ? (
                            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job Name</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredJobLogs.map((log, idx) => (
                                            <tr key={log.id || idx}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(log.timestamp).toLocaleString()}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.jobName}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${log.type === 'ERROR' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                                        {log.type || 'INFO'}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{log.message}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-gray-500">No logs found for this customer.</div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal for displaying detailed order data */}
            {
                isModalOpen && selectedData && createPortal(
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[100] flex items-center justify-center p-4">
                        <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-semibold text-gray-800">{modalTitle} (<span className="text-primary">{selectedData.length}</span> {t?.admin_orders_dashboard || 'Orders'})</h3>
                                <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-full focus:outline-none transition-colors">
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                                </button>
                            </div>
                            <div className="overflow-x-auto overflow-y-auto flex-grow rounded-lg border border-gray-200">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-primary sticky top-0 z-10 shadow-sm">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.date || 'Date'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.customer_name || 'Customer'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.order_user || 'Order User'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.ticket_key || 'Order ID'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.payment_details || 'Method'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.total_amount || 'Amount'}</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">{t?.status || 'Status'}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {selectedData.map((order, index) => (
                                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {order.customerName || order.customerDomain || order.customerId || 'Unknown'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {order.orderUser || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {order.id || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                                    <div className="flex items-center gap-2">
                                                        {['paypal', 'stripe', 'card'].includes(order.paymentMethod?.toLowerCase()) && (
                                                            <img
                                                                src={`/${order.paymentMethod.toLowerCase()}-logo.png`}
                                                                alt={order.paymentMethod}
                                                                className="h-4 w-auto object-contain"
                                                            />
                                                        )}
                                                        {order.paymentMethod || 'Unknown'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800">
                                                    €{order.totalPrice ? order.totalPrice.toFixed(2) : '0.00'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${order.status?.toLowerCase().includes('success') || order.status?.toLowerCase().includes('completed') || order.status?.toLowerCase().includes('paid') ? 'bg-green-100 text-green-800 border border-green-200' : order.status?.toLowerCase().includes('pending') ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
                                                        {order.status || 'Unknown'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

export default AdminCustomerDetails;
